import React, { useEffect, useState } from 'react';
import { HeartIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { billingApi, BillingConfig } from '../api/billing';
import { loadPaddle, setPaddleEventHandler, PaddleEvent } from '../utils/paddle';

/**
 * "Become a Supporter" CTA (#666). Renders nothing unless the backend reports
 * billing as fully configured (so it stays hidden while billing is inert) and
 * the user is not already a supporter. Opens Paddle.js checkout on click; the
 * Paddle webhook grants supporter status server-side. Web-only by design, to
 * avoid the Apple/Google 30% in-app-purchase cut.
 */
const SupporterCheckout: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const toast = useToast();
  const [config, setConfig] = useState<BillingConfig | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    billingApi
      .getConfig()
      .then((res) => {
        if (active) setConfig(res.data);
      })
      .catch(() => {
        if (active) setConfig({ enabled: false });
      });
    return () => {
      active = false;
    };
  }, []);

  // Detach the shared Paddle event handler when this component goes away.
  useEffect(() => () => setPaddleEventHandler(null), []);

  // Hidden while billing is inert / misconfigured, or the user already supports.
  if (!config?.enabled || !config.client_token || !config.price_id) {
    return null;
  }
  // Require a loaded user: opening checkout without user.id would produce a
  // payment the webhook can't map back to an account.
  if (!user || user.is_supporter) {
    return null;
  }

  const clientToken = config.client_token;
  const priceId = config.price_id;

  const handleSupport = async () => {
    setBusy(true);
    try {
      const paddle = await loadPaddle(clientToken, config.environment || 'production');
      setPaddleEventHandler((event: PaddleEvent) => {
        if (event.name === 'checkout.completed') {
          toast.success(
            'Thank you for supporting Fawayo!',
            'Your supporter status will appear shortly.'
          );
          // The webhook grants supporter status server-side; give it a moment
          // to land, then refresh the profile so the badge shows.
          window.setTimeout(() => {
            refreshUser();
          }, 4000);
        }
      });
      paddle.Checkout.open({
        items: [{ priceId, quantity: 1 }],
        customData: user?.id ? { user_id: user.id } : undefined,
        customer: user?.email ? { email: user.email } : undefined,
        settings: { displayMode: 'overlay' },
      });
    } catch {
      toast.error('Could not open checkout', 'Please try again in a moment.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white shadow rounded-lg mb-8">
      <div className="px-4 py-5 sm:p-6">
        <div className="flex items-start">
          <HeartIcon className="h-6 w-6 text-amber-500 mr-3 flex-shrink-0" />
          <div className="flex-1">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Support Fawayo</h3>
            <p className="mt-1 text-sm text-gray-600">
              Fawayo is free forever. Become a supporter to get a badge and help keep the
              lights on — no features are locked behind it.
            </p>
            <button
              type="button"
              onClick={handleSupport}
              disabled={busy}
              className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-amber-600 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 disabled:opacity-50"
            >
              {busy ? 'Opening…' : '🎖️ Become a Supporter'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupporterCheckout;
